import * as XLSX from 'xlsx'; 
import { jsPDF } from 'jspdf';
import 'jspdf-autotable'; 

declare module 'jspdf' {
    interface jsPDF {
        autoTable: (options: any) => jsPDF;
    }
}

// Export data to Excel
export const exportToExcel = (data: any[], fileName: string) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
};

// Export data to PDF
export const exportToPDF = (data: any[], fileName: string) => {
    if (!data || data.length === 0) return;

    const doc = new jsPDF("l", "pt", "a4");

    const columns = Object.keys(data[0]);
    const rows = data.map(item => columns.map(col => item[col] ?? ""));

    // Calculate fixed column width (equal for all)
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const usableWidth = pageWidth - margin * 2;
    const columnWidth = usableWidth / columns.length;

    doc.autoTable({
        head: [columns],
        body: rows,

        startY: 30,
        theme: "grid",         // Full borders

        styles: {
            fontSize: 9,
            cellPadding: 3,
            overflow: "linebreak",
            lineWidth: 0.8,
            lineColor: [0, 0, 0],

            halign: "center",   // Horizontal center
            valign: "middle",   // Vertical center

            minCellHeight: 25   // Same height for all rows
        },

        headStyles: {
            fillColor: [30, 30, 30],
            textColor: 255,
            fontStyle: "bold",
            halign: "center",
            valign: "middle",
            minCellHeight: 30,
            lineWidth: 1,
        },

        bodyStyles: {
            halign: "center",
            valign: "middle",
            minCellHeight: 25,
        },

        // Fixed width for each column
        columnStyles: {
            ...columns.reduce((acc : any, col, i : any) => {
                acc[i] = { cellWidth: columnWidth };
                return acc;
            }, {})
        },

        margin: { left: margin, right: margin },

        didDrawPage: () => {
            doc.setFontSize(14);
            doc.text(fileName, margin, 20);
        },
    });

    doc.save(`${fileName}.pdf`);
};



// Export data to CSV
export const exportToCSV = (data: any[], fileName: string) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const csvOutput = XLSX.utils.sheet_to_csv(worksheet);
    const blob = new Blob([csvOutput], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${fileName}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
};


export const exportPfToPDF = (
    data: any[],
    fileName: string,
    options?: {
        employeeName?: string;
        pfNumber?: string;
    }
) => {
    const doc = new jsPDF();
    const currentDate = new Date().toLocaleDateString();

    // Add header title
    doc.setFontSize(16);
    doc.text('Provident Fund Passbook', doc.internal.pageSize.getWidth() / 2, 15, { align: 'center' });

    // Add employee info
    doc.setFontSize(11);
    const empName = options?.employeeName || 'Employee Name';
    const pfNumber = options?.pfNumber || 'N/A';
    doc.text(`Name: ${empName}`, 14, 25);
    doc.text(`PF Account Number: ${pfNumber}`, 14, 32);

    // Add table
    doc.autoTable({
        startY: 40,
        head: [Object.keys(data[0])],
        body: data.map(row => Object.values(row)),
        styles: {
            fontSize: 10,
        },
        theme: 'grid',
    });

    // Add footer with download date
    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(10);
    doc.text(`Downloaded on: ${currentDate}`, 14, pageHeight - 10);

    doc.save(`${fileName}.pdf`);
};